import { useCallback, useRef } from 'react';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import { LegacyTertiaryButton } from 'components/Button/LegacyTertiaryButton';
import { Hide } from 'components/Hide';
import { Image } from 'components/Image/Image';
import { Body } from 'components/Text/Body';
import { BodySmall } from 'components/Text/BodySmall';
import { H2 } from 'components/Text/H2';
import { Tile } from 'components/Tile';
import { useElementReachedEffect } from 'hooks/viewport/useElementReachedEffect';
import { ListingType } from 'types/ListingType';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEventOnce } from 'utils/analytics/track/trackEventOnce';
import { LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE } from 'utils/constants/general/listingTypeHumanNameMapTitle';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypePluralHumanNameMap';
import { getStaticImageSrc } from 'utils/getImageSrc';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  listingUrl: string;
  type: ListingType;
};

export function ListingViewSignupCta({ listingUrl, type }: Props) {
  const elementReachedRef = useRef<HTMLDivElement>(null);

  useElementReachedEffect({
    ref: elementReachedRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: Job Listing CTA block');
    }, []),
  });

  return (
    <Box data-qa-id="job-listing-signup-promo" ref={elementReachedRef}>
      <Tile
        gradientBorder
        position="relative"
        display="flex"
        flex="1"
        py={5}
        px={4}
        borderRadius="4px"
      >
        <Box display="flex" justifyContent="center" width={[0, 0, '50%']}>
          <Hide xs sm>
            <Image
              src={getStaticImageSrc('planning.svg')}
              alt="Illustration"
              expandSize
            />
          </Hide>
        </Box>
        <Box
          textAlign={['center', 'center', 'left']}
          width={['100%', '100%', '50%']}
        >
          <H2 mb={3}>
            {type === 'JOB'
              ? getText('Take the Next Step in Your Career')
              : getText('Discover Your Calling')}
          </H2>
          <Body mb={3}>
            {type === 'JOB'
              ? getText(
                  'Match with social-impact hiring managers, explore the latest job opportunities, and get notified when new opportunities meet your search criteria.',
                )
              : sprintf(
                  getText(
                    'Find opportunities to change the world with the latest social-impact job, internship, and volunteer listings. Plus, explore resources for taking action in your community.',
                  ),
                  { type: LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[type] },
                )}
          </Body>

          <Box mb={3}>
            <PrimaryButton
              onClick={(e) => {
                e.preventDefault();
                trackClicked(
                  `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Sign Up`,
                );
                showModal('SIGNUP', {
                  redirectTo: listingUrl,
                });
              }}
              data-qa-id="listing-promo-signup-link"
            >
              {getText('Sign Up Now')}
            </PrimaryButton>
          </Box>

          <LegacyTertiaryButton
            onClick={() => {
              trackClicked(
                `${LISTING_TYPE_HUMAN_TRACKING_NAME_MAP_TITLE[type]} Listing CTA: Log In`,
              );
              showModal('LOGIN', {
                redirectTo: listingUrl,
              });
            }}
            data-qa-id="listing-promo-login-link"
          >
            <BodySmall styleAs="h5">
              {getText('Already a user? Log in')}
            </BodySmall>
          </LegacyTertiaryButton>
        </Box>
      </Tile>
    </Box>
  );
}
